import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Banner,
  Card,
  DefinitionList,
  DefinitionRow,
  IdChip,
  InfoHint,
  RewardPill,
  StatusPill,
  Table,
  TCell,
  THead,
  TRow,
} from '@cou/admin-ui';
import { AdminShell } from '../shell/AdminShell';
import { useAdminAuth } from '../session/admin-auth-context';
import {
  defaultEntriesClient,
  type AdminEntryDetail,
  type EntriesClient,
} from '../entries/entries-client';
import { toRewardType } from '../prizes/reward-cell';
import { entryPillState } from '../entries/entry-state';
import { pickDetails } from '../entries/pick-details';
import { formatDateParts } from '../contests/format-date';

/**
 * One Entry with its Picks (API-22, admin-ui.md): the last hop of the support flow — account ref →
 * player → entries → picks. Read-only: entry and pick voids live in the settlement tooling behind
 * `contests:settle` (API-11), so there is deliberately no action on this screen.
 *
 * Every Pick renders from the frozen Contest snapshot (the market type, the subject, the Line as it
 * was at finalisation), so an Entry from months ago reads exactly as the player saw it, long after
 * the read-path blobs rolled on (ADR-0004, INV-04).
 */

export interface EntryDetailScreenProps {
  /** Injected for tests; defaults to the real API-22 client. */
  readonly client?: EntriesClient;
}

export function EntryDetailScreen({ client = defaultEntriesClient }: EntryDetailScreenProps) {
  const { id } = useParams<{ id: string }>();
  const { getToken } = useAdminAuth();
  const [entry, setEntry] = useState<AdminEntryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = getToken();
    if (token === null || id === undefined) return;
    const result = await client.getEntry(token, id);
    if (result.ok) {
      setEntry(result.entry);
      setError(null);
    } else {
      setError(result.message ?? 'The entry lookup failed.');
      setEntry(null);
    }
  }, [client, getToken, id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null) {
    return (
      <AdminShell title="Entry">
        <Banner data-testid="entry-error" variant="danger">
          {error}
        </Banner>
      </AdminShell>
    );
  }

  if (entry === null) return <AdminShell title="Entry" />;

  return (
    <AdminShell
      title="Entry"
      meta={<StatusPill kind="entry" state={entryPillState(entry.status)} />}
    >
      <Card data-testid="entry-header" className="mt-4 p-4">
        <DefinitionList>
          <DefinitionRow label="Account ref" value={<IdChip value={entry.account} />} />
          {/* The Segment as snapshotted at submission — the column of the payout matrix this Entry
              was actually graded against, not the player's segment today. Read by its registry
              label, as Players and the Awards queue show it; the raw ref stands in when the registry
              has no label, so the two surfaces reconcile (CAE-559). */}
          <DefinitionRow
            label="Segment (snapshot)"
            value={entry.segmentLabel ?? entry.segmentSnapshot ?? 'default'}
          />
          <DefinitionRow label="Correct picks" value={entry.correctPicks} />
          <DefinitionRow label="Tier" value={entry.tier ?? '—'} />
          <DefinitionRow
            label="Reward"
            value={
              entry.reward === null ? (
                '—'
              ) : (
                <RewardPill
                  type={toRewardType(entry.prizeType) ?? 'freebet'}
                  label={[entry.reward.value, entry.reward.name].filter(Boolean).join(' ')}
                />
              )
            }
          />
          <DefinitionRow
            label="Submitted"
            value={
              entry.submittedAt === null
                ? '—'
                : [
                    formatDateParts(entry.submittedAt).date,
                    formatDateParts(entry.submittedAt).time,
                  ].join(' · ')
            }
          />
          <DefinitionRow
            label="Locked"
            value={
              entry.lockedAt === null
                ? '—'
                : [formatDateParts(entry.lockedAt).date, formatDateParts(entry.lockedAt).time].join(
                    ' · ',
                  )
            }
          />
        </DefinitionList>
      </Card>

      <h2 className="mt-8 text-base font-semibold text-ink">Picks</h2>
      <Table className="mt-3" columns="minmax(0,1.2fr) 140px minmax(0,1.4fr) 130px 90px 110px">
        <THead>
          <TCell>Subject</TCell>
          <TCell>Market</TCell>
          <TCell>Event</TCell>
          <TCell>Call</TCell>
          <TCell>Final</TCell>
          <TCell>Result</TCell>
        </THead>
        {entry.picks.map((pick) => (
          <TRow key={pick.id} data-testid={`entry-pick-${pick.id}`}>
            {/* One hint for the whole row, on the first column: every id an operator needs to
                quote this Pick belongs to the same Pick, so it is one place to look. */}
            <TCell variant="primary">
              <InfoHint entity="Pick" details={pickDetails(pick)}>
                <span className="min-w-0 truncate">{pick.athlete.name}</span>
              </InfoHint>
            </TCell>
            <TCell>{pick.marketType}</TCell>
            <TCell>{pick.eventName}</TCell>
            {/* The call as made: the side, against the Line frozen at finalisation. */}
            <TCell variant="mono" data-testid="pick-call">
              {pick.direction} {pick.frozenLine}
            </TCell>
            <TCell variant="num" data-testid="pick-final">
              {pick.finalValue ?? '—'}
            </TCell>
            <TCell data-testid="pick-result" state={pick.result === 'VOID' ? 'void' : undefined}>
              {pickResultText(pick.result)}
            </TCell>
          </TRow>
        ))}
      </Table>
    </AdminShell>
  );
}

/**
 * A Pick's result as an operator reads it. A VOID is shown as void — never as a loss: the player
 * made no wrong call, the market did (CONTEXT → Void), and rendering it red would misread the
 * Entry for whoever is answering the player's question.
 */
function pickResultText(result: string | null): string {
  if (result === null) return '—';
  if (result === 'VOID') return 'Void';
  return result === 'WIN' ? 'Win' : 'Loss';
}
